import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Check, Minus, Plus } from 'lucide-react';
import { textIncludesQuery, textsEqualForSearch } from '@narrativebench/editor-core';
import { cn } from '../lib/utils';
import { useLocale } from '../contexts/LocaleContext';
import {
  cycleFilterOptionPolarity,
  formatIncludeExcludeCount,
  getFilterOptionPolarity,
} from '../utils/filter-option-polarity';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export interface MultiSelectFilterOption {
  id: string;
  name: string;
}

interface MultiSelectFilterContentProps {
  label: string;
  options: MultiSelectFilterOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  excludedIds?: string[];
  onExcludedChange?: (ids: string[]) => void;
  allowExclude?: boolean;
  footer?: React.ReactNode;
  createLabel?: (query: string) => string;
}

export const MultiSelectFilterContent: React.FC<MultiSelectFilterContentProps> = ({
  label,
  options,
  selectedIds,
  onChange,
  excludedIds = [],
  onExcludedChange,
  allowExclude = false,
  footer,
  createLabel,
}) => {
  const { t } = useLocale();
  const [search, setSearch] = useState('');
  const activeExcludedIds = allowExclude ? excludedIds : [];

  const filteredOptions = useMemo(() => {
    if (!search.trim()) {
      return options;
    }
    return options.filter((option) => textIncludesQuery(option.name, search));
  }, [options, search]);

  const normalizedSearch = search.trim().toLowerCase();
  const canCreateOption = Boolean(
    createLabel &&
    normalizedSearch.length > 0 &&
    !options.some((option) => option.id === normalizedSearch || textsEqualForSearch(option.name, search)) &&
    !selectedIds.includes(normalizedSearch) &&
    !activeExcludedIds.includes(normalizedSearch)
  );

  const applySelection = (nextIncluded: string[], nextExcluded: string[]): void => {
    onChange(nextIncluded);
    if (allowExclude) {
      onExcludedChange?.(nextExcluded);
    }
  };

  const toggleOption = (id: string): void => {
    if (allowExclude) {
      const next = cycleFilterOptionPolarity(id, selectedIds, activeExcludedIds);
      applySelection(next.includedIds, next.excludedIds);
      return;
    }
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const createFromSearch = (): void => {
    if (!canCreateOption) {
      return;
    }
    applySelection(
      [...selectedIds.filter((selectedId) => selectedId !== normalizedSearch), normalizedSearch],
      activeExcludedIds.filter((excludedId) => excludedId !== normalizedSearch)
    );
    setSearch('');
  };

  const clearAll = (): void => {
    applySelection([], []);
  };

  const hasActiveSelection = selectedIds.length > 0 || activeExcludedIds.length > 0;

  return (
    <>
      <div className="mb-2 flex items-center justify-between px-1">
        <span className="text-sm font-semibold text-foreground">{label}</span>
        {hasActiveSelection && (
          <button
            type="button"
            className="text-xs font-medium text-scribane-green hover:underline"
            onClick={clearAll}
          >
            {t('clear_all')}
          </button>
        )}
      </div>
      {allowExclude && (
        <p className="mb-2 px-1 text-xs text-muted-foreground">{t('filter_include_exclude_hint')}</p>
      )}
      <Input
        type="text"
        placeholder={t('select_one_or_more_options')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter' && canCreateOption) {
            e.preventDefault();
            createFromSearch();
          }
        }}
        className="mb-1"
      />
      {canCreateOption && createLabel && (
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            createFromSearch();
          }}
        >
          <Plus className="h-4 w-4" />
          {createLabel(normalizedSearch)}
        </DropdownMenuItem>
      )}
      <div className="max-h-[260px] overflow-auto">
        {filteredOptions.length === 0 && !canCreateOption ? (
          <div className="px-2 py-3 text-center text-sm text-muted-foreground">{t('no_options_found')}</div>
        ) : (
          filteredOptions.map((option) => {
            const polarity = getFilterOptionPolarity(option.id, selectedIds, activeExcludedIds);
            const isIncluded = polarity === 'include';
            const isExcluded = polarity === 'exclude';
            return (
              <DropdownMenuItem
                key={option.id}
                onSelect={(e) => {
                  e.preventDefault();
                  toggleOption(option.id);
                }}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border border-foreground/50',
                    isIncluded && 'border-foreground bg-foreground text-background',
                    isExcluded && 'border-destructive bg-destructive text-destructive-foreground'
                  )}
                  aria-hidden
                >
                  {isIncluded && <Check className="h-3 w-3" strokeWidth={2.5} />}
                  {isExcluded && <Minus className="h-3 w-3" strokeWidth={2.5} />}
                </span>
                <span className={cn(isExcluded && 'text-muted-foreground line-through')}>
                  {option.name}
                </span>
              </DropdownMenuItem>
            );
          })
        )}
      </div>
      {footer}
    </>
  );
};

interface MultiSelectFilterDropdownProps {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  options: MultiSelectFilterOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  excludedIds?: string[];
  onExcludedChange?: (ids: string[]) => void;
  allowExclude?: boolean;
  className?: string;
  footer?: React.ReactNode;
  createLabel?: (query: string) => string;
  disabled?: boolean;
}

export const MultiSelectFilterDropdown: React.FC<MultiSelectFilterDropdownProps> = ({
  label,
  icon: Icon,
  options,
  selectedIds,
  onChange,
  excludedIds = [],
  onExcludedChange,
  allowExclude = false,
  className,
  footer,
  createLabel,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const activeExcludedIds = allowExclude ? excludedIds : [];
  const countLabel = formatIncludeExcludeCount(selectedIds.length, activeExcludedIds.length);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={(open) => {
        if (disabled) {
          setIsOpen(false);
          return;
        }
        setIsOpen(open);
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-[38px] shrink-0 items-center gap-2 rounded-md border border-input bg-card px-4 text-sm shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-60',
            countLabel && 'border-foreground/40 bg-accent/50',
            className
          )}
        >
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span>{label}</span>
          {countLabel && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground px-1.5 text-xs font-medium text-background">
              {countLabel}
            </span>
          )}
          <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', isOpen && 'rotate-180')} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[260px] p-2">
        <MultiSelectFilterContent
          label={label}
          options={options}
          selectedIds={selectedIds}
          onChange={onChange}
          excludedIds={excludedIds}
          onExcludedChange={onExcludedChange}
          allowExclude={allowExclude}
          footer={footer}
          createLabel={createLabel}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
